import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  exhibitionColumns,
  exhibitionColumnsVisibility,
} from "@/config/dashboard/exhibition";
import { generateTable } from "@/config/dashboard/general";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { Exhibition } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { ExhibitionLinkGridCard } from "./relatedLinkGridCards";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ExhibitionLinks component
 * @returns {JSX.Element}
 */
const ExhibitionLinks = ({ id, type }: LinksProps) => {
  const ACTIONS_COLUMN_ID = "actions";
  const ensureActionsColumnLast = (order: string[]) => {
    const uniqueOrder = Array.from(new Set(order));
    const withoutActions = uniqueOrder.filter(
      (columnId) => columnId !== ACTIONS_COLUMN_ID,
    );
    return [...withoutActions, ACTIONS_COLUMN_ID];
  };

  const [getPaginationRelatedExhibitions, setPaginationRelatedExhibitions] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedExhibition",
    getPaginationRelatedExhibitions.pageSize,
    getPaginationRelatedExhibitions.page *
      getPaginationRelatedExhibitions.pageSize,
  );

  const { data: countRelatedExhibitions } = useCountRelatedFields(id, type, [
    "relatedExhibition",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedExhibition",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedExhibition",
  );

  const { t } = useTranslation();

  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedExhibitionId, setSelectedExhibitionId] = useState<string | null>(
    null,
  );

  const toggleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedExhibitions } = useGetNotRelatedResource<Exhibition>({
    resultType: "exhibition",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteRelation(id);
    },
    [deleteRelation],
  );

  const baseColumns = useMemo(
    () => generateTable(exhibitionColumns, "exhibition", t),
    [t],
  );

  const renderExhibitionRowActions = useCallback(
    (item: Exhibition) => (
      <RelatedRecordRowActions
          isAddMode={isAddMode}
          resourceId={item.id}
          previewType="exhibition"
          previewElements={[
            {
              label: t("expositionsDescriptionNumeroExpo"),
              value: item.expositionsDescriptionNumeroExpo,
              type: "text",
            },
            {
              label: t("attachments"),
              value: item.attachments,
              type: "image",
            },
            {
              label: t("expositionsDescriptionResume"),
              value: item.expositionsDescriptionResume,
              type: "richtext",
            },
          ]}
          onDelete={() => handleDeleteRelation(item.id)}
          onAdd={() => handleCreateRelation(item.id)}
        />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Exhibition>(({ row }) =>
        renderExhibitionRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderExhibitionRowActions]);

  const STORAGE_KEY = "exhibition-links-table";
  const defaultVisibility = { ...exhibitionColumnsVisibility, actions: true };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order, columnOrder } = saved;
        const loadedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : [];
        return ensureActionsColumnLast(loadedOrder as string[]);
      }
    } catch {
      // ignore
    }
    return ensureActionsColumnLast([]);
  });

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedExhibitions,
    setPage: setPaginationRelatedExhibitions,
    totalSize: countRelatedExhibitions?.relatedExhibition ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("linkedExhibitions")}
      isAddMode={isAddMode}
      onBack={toggleAddMode}
      onToggleAdd={toggleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notRelatedExhibitions?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedExhibitions?.totalSize ?? 0,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={relatedFields ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {relatedFields?.map((row: Exhibition) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderExhibitionRowActions(row)}>
                <ExhibitionLinkGridCard
                item={row}
                t={t}
                onTextClick={() => setSelectedExhibitionId(row.id)}
              />
              </GridCardWithActions>
              <ShowElements
                type="exhibition"
                id={row.id}
                open={selectedExhibitionId === row.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedExhibitionId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("expositionsDescriptionNumeroExpo"),
                    value: row.expositionsDescriptionNumeroExpo,
                    type: "text",
                  },
                  {
                    label: t("expositionsDescriptionResume"),
                    value: row.expositionsDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default ExhibitionLinks;
